<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
import { encodeId } from '@/utils/obfuscate'

const route = useRoute()
const router = useRouter()

const scope = ref(route.query.scope || 'activas')       // 'activas' | 'por-vencer'
const days = ref(Number(route.query.days || 7))

const page = ref(Number(route.query.page || 1))
const limit = ref(Number(route.query.limit || 10)) // 10 por página

const loading = ref(false)
const rows = ref([])
const total = ref(0)
const totalPages = computed(() => Math.max(Math.ceil(total.value / limit.value), 1))
const errorMsg = ref('')

async function loadData() {
    loading.value = true
    errorMsg.value = ''
    try {
        const { data } = await api.get('/dashboard/dashboard/membresias', {
            params: { scope: scope.value, days: days.value, page: page.value, limit: limit.value }
        })
        rows.value = data.items || []
        total.value = Number(data.total || 0)
        // sincroniza por si el backend ajustó algo
        page.value = Number(data.page || page.value)
        limit.value = Number(data.per_page || limit.value)
    } catch (e) {
        errorMsg.value = e?.response?.data?.message || e.message || 'Error al cargar'
        Swal.fire('Error', errorMsg.value, 'error')
    } finally {
        loading.value = false
    }
}

function goSocio(id) {
    router.push({ name: 'SocioDetalle', params: { id: encodeId(id) } })
}

watch([scope, days, page, limit], () => {
    router.replace({
        query: {
            scope: scope.value,
            days: days.value,
            page: page.value,
            limit: limit.value
        }
    })
    loadData()
})
watch([scope, days], () => { page.value = 1 })
onMounted(loadData)
</script>

<template>
    <div class="container-fluid py-4">
        <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
                <h5 class="mb-0">Membresías — {{ scope === 'por-vencer' ? 'Por vencer' : 'Activas' }}</h5>
                <small class="text-muted">Total: {{ total }}</small>
            </div>

            <div class="ms-auto d-flex gap-2">
                <select v-model="scope" class="form-select">
                    <option value="activas">Activas</option>
                    <option value="por-vencer">Por vencer</option>
                </select>

                <input v-if="scope === 'por-vencer'" v-model.number="days" type="number" min="1" max="60"
                    class="form-control" style="width:120px" />
            </div>
        </div>

        <div class="card p-3">
            <div v-if="loading" class="text-muted">Cargando…</div>
            <div v-else>
                <div v-if="rows.length === 0" class="text-muted">Sin resultados.</div>

                <div v-else class="table-responsive">
                    <table class="table table-sm align-middle">
                        <thead class="table-light">
                            <tr>
                                <th style="width:56px;"></th>
                                <th>Socio</th>
                                <th>Identificación</th>
                                <th>Email</th>
                                <th>Teléfono</th>
                                <th>Inicio</th>
                                <th>Fin</th>
                                <th>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in rows" :key="r.membresia_usuario_id">
                                <td>
                                    <div class="avatar-wrapper">
                                        <img v-if="r.avatar_url" :src="r.avatar_url" class="avatar-img" alt="">
                                        <div v-else class="avatar-fallback">?</div>
                                    </div>
                                </td>
                                <td class="fw-semibold">{{ r.socio }}</td>
                                <td>{{ r.identificacion || '—' }}</td>
                                <td>{{ r.email || '—' }}</td>
                                <td>{{ r.telefono || '—' }}</td>
                                <td>{{ r.fecha_inicio }}</td>
                                <td>{{ r.fecha_fin }}</td>
                                <td>
                                    <span class="badge bg-success" v-if="r.estado === 'activa'">ACTIVA</span>
                                    <span class="badge bg-secondary" v-else>{{ r.estado }}</span>
                                </td>
                                <td class="text-end">
                                    <button class="btn btn-sm btn-primary" @click="goSocio(r.usuario_id)">Ver</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- 🔹 Paginación -->
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <div class="text-muted small">
                        Página {{ page }} de {{ totalPages }} — Mostrando {{ rows.length }} de {{ total }}
                    </div>

                    <nav class="d-flex gap-2">
                        <button class="btn btn-sm btn-outline-secondary" :disabled="page <= 1"
                            @click="page--">Anterior</button>
                        <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages"
                            @click="page++">Siguiente</button>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.table td,
.table th {
    font-size: 0.85rem;
}

.avatar-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    background: #f0f2f5;
    display: grid;
    place-items: center
}

.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover
}

.avatar-fallback {
    font-weight: 600;
    color: #445
}
</style>
